import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Pencil, Minus, Square, Circle, Eraser, Trash2, Power, X, GripHorizontal } from "lucide-react";

const COLORS = ["#000000", "#ef4444", "#3b82f6", "#22c55e", "#eab308", "#f97316", "#a855f7", "#ffffff"];
const WIDTHS = [2, 4, 6, 10];

const TOOLS = [
  { id: "pencil", icon: Pencil, label: "Pencil" },
  { id: "line", icon: Minus, label: "Line" },
  { id: "rectangle", icon: Square, label: "Rectangle" },
  { id: "circle", icon: Circle, label: "Circle" },
  { id: "eraser", icon: Eraser, label: "Eraser" },
];

const BTN_SIZE = 36;
const PEEK = 18;
const EDGE_THRESHOLD = 40;
const ERASER_RADIUS = 12; // hit detection radius for object eraser

// Distance from point to line segment
function distToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

// Check if point (px,py) hits a drawing object
function hitTest(drawing, px, py) {
  const threshold = ERASER_RADIUS + (drawing.width || 2) / 2;

  if (drawing.tool === "pencil") {
    if (!drawing.points || drawing.points.length === 0) return false;
    for (let i = 0; i < drawing.points.length - 1; i++) {
      if (distToSegment(px, py, drawing.points[i].x, drawing.points[i].y,
          drawing.points[i + 1].x, drawing.points[i + 1].y) <= threshold) return true;
    }
    if (drawing.points.length === 1) {
      return Math.hypot(px - drawing.points[0].x, py - drawing.points[0].y) <= threshold;
    }
    return false;
  }

  if (drawing.tool === "line") {
    return distToSegment(px, py, drawing.start.x, drawing.start.y,
        drawing.end.x, drawing.end.y) <= threshold;
  }

  if (drawing.tool === "rectangle") {
    const x1 = Math.min(drawing.start.x, drawing.end.x);
    const y1 = Math.min(drawing.start.y, drawing.end.y);
    const x2 = Math.max(drawing.start.x, drawing.end.x);
    const y2 = Math.max(drawing.start.y, drawing.end.y);
    // Check all 4 edges
    if (distToSegment(px, py, x1, y1, x2, y1) <= threshold) return true; // top
    if (distToSegment(px, py, x2, y1, x2, y2) <= threshold) return true; // right
    if (distToSegment(px, py, x2, y2, x1, y2) <= threshold) return true; // bottom
    if (distToSegment(px, py, x1, y2, x1, y1) <= threshold) return true; // left
    return false;
  }

  if (drawing.tool === "circle") {
    const cx = (drawing.start.x + drawing.end.x) / 2;
    const cy = (drawing.start.y + drawing.end.y) / 2;
    const rx = Math.abs(drawing.end.x - drawing.start.x) / 2 || 1;
    const ry = Math.abs(drawing.end.y - drawing.start.y) / 2 || 1;
    // Normalized distance from ellipse boundary
    const ndx = (px - cx) / rx, ndy = (py - cy) / ry;
    const dist = Math.sqrt(ndx * ndx + ndy * ndy);
    const avgR = (rx + ry) / 2;
    return Math.abs(dist - 1) * avgR <= threshold;
  }

  return false;
}

function loadDrawings(pathname) {
  try {
    const all = JSON.parse(localStorage.getItem("pents_drawings") || "{}");
    return all[pathname] || [];
  } catch { return []; }
}

function saveDrawings(pathname, drawings) {
  try {
    const all = JSON.parse(localStorage.getItem("pents_drawings") || "{}");
    if (drawings.length === 0) delete all[pathname];
    else all[pathname] = drawings;
    localStorage.setItem("pents_drawings", JSON.stringify(all));
  } catch {}
}

function getEdgeSnap(x, y) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const dL = x, dR = w - x, dT = y, dB = h - y;
  const min = Math.min(dL, dR, dT, dB);
  if (min === dL) return { edge: "left", pos: Math.max(PEEK, Math.min(y, h - PEEK)) };
  if (min === dR) return { edge: "right", pos: Math.max(PEEK, Math.min(y, h - PEEK)) };
  if (min === dT) return { edge: "top", pos: Math.max(PEEK, Math.min(x, w - PEEK)) };
  return { edge: "bottom", pos: Math.max(PEEK, Math.min(x, w - PEEK)) };
}

function getCollapsedStyle(edge, pos) {
  const half = BTN_SIZE / 2;
  switch (edge) {
    case "left": return { left: -(half - PEEK), top: pos - half };
    case "right": return { right: -(half - PEEK), top: pos - half };
    case "top": return { top: -(half - PEEK), left: pos - half };
    case "bottom": return { bottom: -(half - PEEK), left: pos - half };
    default: return { right: -(half - PEEK), top: pos - half };
  }
}

function isAtEdge(x, y) {
  return x <= EDGE_THRESHOLD || x >= window.innerWidth - EDGE_THRESHOLD ||
         y <= EDGE_THRESHOLD || y >= window.innerHeight - EDGE_THRESHOLD;
}

function loadEdgePos() {
  try {
    const s = JSON.parse(localStorage.getItem("pents_edge_pos"));
    if (s && s.edge && s.pos != null) {
      // Saved on a bigger window → clamp back on-screen
      const max = (s.edge === "top" || s.edge === "bottom" ? window.innerWidth : window.innerHeight) - PEEK;
      return { edge: s.edge, pos: Math.max(PEEK, Math.min(s.pos, max)) };
    }
  } catch {}
  return { edge: "right", pos: window.innerHeight / 2 };
}

function loadPanelPos() {
  try {
    const s = JSON.parse(localStorage.getItem("pents_panel_pos"));
    if (s && s.x != null && s.y != null) return s;
  } catch {}
  return { x: window.innerWidth - 230, y: window.innerHeight / 2 - 130 };
}

function renderDrawing(ctx, drawing, offsetX, offsetY) {
  if (drawing.tool === "eraser") return; // eraser doesn't draw, it removes objects
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.lineWidth = drawing.width;
  ctx.globalCompositeOperation = "source-over";
  ctx.strokeStyle = drawing.color;
  ctx.beginPath();
  if (drawing.tool === "pencil") {
    if (drawing.points && drawing.points.length > 0) {
      ctx.moveTo(drawing.points[0].x - offsetX, drawing.points[0].y - offsetY);
      for (let i = 1; i < drawing.points.length; i++)
        ctx.lineTo(drawing.points[i].x - offsetX, drawing.points[i].y - offsetY);
      ctx.stroke();
    }
  } else if (drawing.tool === "line") {
    ctx.moveTo(drawing.start.x - offsetX, drawing.start.y - offsetY);
    ctx.lineTo(drawing.end.x - offsetX, drawing.end.y - offsetY);
    ctx.stroke();
  } else if (drawing.tool === "rectangle") {
    const sx = drawing.start.x - offsetX, sy = drawing.start.y - offsetY;
    const ex = drawing.end.x - offsetX, ey = drawing.end.y - offsetY;
    ctx.strokeRect(Math.min(sx, ex), Math.min(sy, ey), Math.abs(ex - sx), Math.abs(ey - sy));
  } else if (drawing.tool === "circle") {
    const sx = drawing.start.x - offsetX, sy = drawing.start.y - offsetY;
    const ex = drawing.end.x - offsetX, ey = drawing.end.y - offsetY;
    const cx = (sx + ex) / 2, cy = (sy + ey) / 2;
    ctx.ellipse(cx, cy, Math.abs(ex - sx) / 2 || 1, Math.abs(ey - sy) / 2 || 1, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalCompositeOperation = "source-over";
}

export default function PentsOverlay() {
  const location = useLocation();
  const canvasRef = useRef(null);
  const drawingsRef = useRef([]);
  const currentStrokeRef = useRef(null);

  const [enabled, setEnabled] = useState(() => localStorage.getItem("setting_pents_enabled") === "true");
  const [drawMode, setDrawMode] = useState(false);
  const [activeTool, setActiveTool] = useState("pencil");
  const [strokeColor, setStrokeColor] = useState("#ef4444");
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [drawings, setDrawings] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState(null);

  // "collapsed" = small icon at edge, "expanded" = floating panel anywhere
  const [mode, setMode] = useState("collapsed");
  const [faded, setFaded] = useState(false);
  const fadeTimerRef = useRef(null);

  // Collapsed: edge position
  const [edgePos, setEdgePos] = useState(loadEdgePos);

  // Expanded: panel position (top-left of panel)
  const [panelPos, setPanelPos] = useState(loadPanelPos);

  // Dragging state for collapsed button
  const [draggingCollapsed, setDraggingCollapsed] = useState(false);
  const [collapsedDragPos, setCollapsedDragPos] = useState(null);
  const collapsedClickRef = useRef(false);

  // Dragging state for expanded panel
  const [draggingPanel, setDraggingPanel] = useState(false);
  const [panelDragPos, setPanelDragPos] = useState(null);
  const panelDragOffset = useRef({ x: 0, y: 0 });
  const [showEdgeHint, setShowEdgeHint] = useState(null); // which edge is being hovered

  useEffect(() => { drawingsRef.current = drawings; }, [drawings]);
  useEffect(() => { currentStrokeRef.current = currentStroke; }, [currentStroke]);
  useEffect(() => { localStorage.setItem("pents_edge_pos", JSON.stringify(edgePos)); }, [edgePos]);
  useEffect(() => { localStorage.setItem("pents_panel_pos", JSON.stringify(panelPos)); }, [panelPos]);

  // Fade timer for collapsed mode
  const resetFadeTimer = useCallback(() => {
    setFaded(false);
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = setTimeout(() => setFaded(true), 5000);
  }, []);

  // Restart on enable too — otherwise the timer runs while Pents is off and the
  // icon appears already faded (white-on-white, effectively invisible)
  useEffect(() => {
    if (!enabled) return;
    if (mode === "collapsed") resetFadeTimer();
    else { setFaded(false); if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current); }
    return () => { if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current); };
  }, [mode, enabled, resetFadeTimer]);

  useEffect(() => {
    const handler = () => setEnabled(localStorage.getItem("setting_pents_enabled") === "true");
    window.addEventListener("settings-changed", handler);
    return () => window.removeEventListener("settings-changed", handler);
  }, []);

  // Alt+P toggles Pents on/off globally (works whether or not it's currently enabled)
  useEffect(() => {
    const onKeyDown = (e) => {
      // e.code is layout-independent — Alt can produce special chars in e.key
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === "KeyP") {
        e.preventDefault();
        const newValue = localStorage.getItem("setting_pents_enabled") !== "true";
        localStorage.setItem("setting_pents_enabled", String(newValue));
        window.dispatchEvent(new Event("settings-changed"));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    setDrawings(loadDrawings(location.pathname));
  }, [location.pathname, enabled]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const sx = window.scrollX, sy = window.scrollY;
    drawingsRef.current.forEach((d) => renderDrawing(ctx, d, sx, sy));
    if (currentStrokeRef.current) renderDrawing(ctx, currentStrokeRef.current, sx, sy);
  }, []);

  useEffect(() => { redraw(); }, [drawings, currentStroke, redraw]);

  useEffect(() => {
    if (!enabled) return;
    const resize = () => {
      const c = canvasRef.current;
      if (!c) return;
      c.width = window.innerWidth;
      c.height = window.innerHeight;
      redraw();
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [enabled, redraw]);

  useEffect(() => {
    if (!enabled) return;
    const onScroll = () => redraw();
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [enabled, redraw]);

  // --- Collapsed button drag ---
  useEffect(() => {
    if (!draggingCollapsed) return;
    const onMove = (e) => {
      const dx = e.clientX - collapsedDragPos.x;
      const dy = e.clientY - collapsedDragPos.y;
      if (dx * dx + dy * dy > 16) collapsedClickRef.current = false;
      setCollapsedDragPos({ x: e.clientX, y: e.clientY });
    };
    const onUp = (e) => {
      setDraggingCollapsed(false);
      if (collapsedClickRef.current) {
        // It was a click - expand the panel
        setMode("expanded");
        // Position panel near where the collapsed button was
        const snap = getEdgeSnap(e.clientX, e.clientY);
        const px = snap.edge === "right" ? window.innerWidth - 230 : snap.edge === "left" ? 20 : e.clientX - 105;
        const py = snap.edge === "bottom" ? window.innerHeight - 290 : snap.edge === "top" ? 20 : e.clientY - 130;
        setPanelPos({ x: Math.max(8, Math.min(px, window.innerWidth - 220)), y: Math.max(8, Math.min(py, window.innerHeight - 280)) });
      } else {
        // It was a drag - snap back to nearest edge
        const snapped = getEdgeSnap(e.clientX, e.clientY);
        setEdgePos(snapped);
      }
      setCollapsedDragPos(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [draggingCollapsed, collapsedDragPos]);

  // --- Expanded panel drag ---
  useEffect(() => {
    if (!draggingPanel) return;
    const onMove = (e) => {
      const nx = e.clientX - panelDragOffset.current.x;
      const ny = e.clientY - panelDragOffset.current.y;
      setPanelDragPos({ x: nx, y: ny });
      // Check if user is pushing into edge
      if (isAtEdge(e.clientX, e.clientY)) {
        const snap = getEdgeSnap(e.clientX, e.clientY);
        setShowEdgeHint(snap.edge);
      } else {
        setShowEdgeHint(null);
      }
    };
    const onUp = (e) => {
      setDraggingPanel(false);
      setPanelDragPos(null);
      // If released at edge → collapse to that edge
      if (isAtEdge(e.clientX, e.clientY)) {
        const snapped = getEdgeSnap(e.clientX, e.clientY);
        setEdgePos(snapped);
        setMode("collapsed");
      } else {
        // Stay expanded, update position
        const nx = e.clientX - panelDragOffset.current.x;
        const ny = e.clientY - panelDragOffset.current.y;
        setPanelPos({ x: Math.max(0, Math.min(nx, window.innerWidth - 210)), y: Math.max(0, Math.min(ny, window.innerHeight - 100)) });
      }
      setShowEdgeHint(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [draggingPanel]);

  const handleCollapsedMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    collapsedClickRef.current = true;
    setDraggingCollapsed(true);
    setCollapsedDragPos({ x: e.clientX, y: e.clientY });
  };

  const handlePanelDragStart = (e) => {
    e.preventDefault();
    const rect = e.currentTarget.closest("[data-pents-panel]").getBoundingClientRect();
    panelDragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDraggingPanel(true);
    setPanelDragPos({ x: rect.left, y: rect.top });
  };

  const handleMinimize = () => {
    // Collapse to nearest edge from current panel center
    const cx = panelPos.x + 105;
    const cy = panelPos.y + 130;
    const snapped = getEdgeSnap(cx, cy);
    setEdgePos(snapped);
    setMode("collapsed");
  };

  // Drawing handlers
  const getDocPos = (e) => ({ x: e.clientX + window.scrollX, y: e.clientY + window.scrollY });

  // Eraser: remove any drawing under cursor
  const eraseAt = useCallback((pos) => {
    const remaining = drawingsRef.current.filter((d) => !hitTest(d, pos.x, pos.y));
    if (remaining.length !== drawingsRef.current.length) {
      setDrawings(remaining);
      saveDrawings(location.pathname, remaining);
    }
  }, [location.pathname]);

  const handleMouseDown = (e) => {
    if (!drawMode) return;
    e.preventDefault();
    const pos = getDocPos(e);

    if (activeTool === "eraser") {
      setIsDrawing(true); // track that we're in erase mode
      eraseAt(pos);
      return;
    }

    setIsDrawing(true);
    if (activeTool === "pencil")
      setCurrentStroke({ tool: activeTool, color: strokeColor, width: strokeWidth, points: [pos] });
    else
      setCurrentStroke({ tool: activeTool, color: strokeColor, width: strokeWidth, start: pos, end: pos });
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;

    if (activeTool === "eraser") {
      eraseAt(getDocPos(e));
      return;
    }

    if (!currentStroke) return;
    const pos = getDocPos(e);
    if (currentStroke.tool === "pencil") {
      const pts = currentStroke.points, last = pts[pts.length - 1];
      const dx = pos.x - last.x, dy = pos.y - last.y;
      if (dx * dx + dy * dy < 9) return;
      setCurrentStroke({ ...currentStroke, points: [...pts, pos] });
    } else {
      setCurrentStroke({ ...currentStroke, end: pos });
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (activeTool === "eraser") return; // eraser already handled inline

    if (!currentStroke) return;
    const nd = [...drawings, currentStroke];
    setDrawings(nd);
    saveDrawings(location.pathname, nd);
    setCurrentStroke(null);
  };

  const handleClear = () => {
    setDrawings([]);
    saveDrawings(location.pathname, []);
    setCurrentStroke(null);
  };

  if (!enabled) return null;

  const pencilRotation = {
    left: "rotate(-45deg)", right: "rotate(135deg)",
    top: "rotate(45deg)", bottom: "rotate(-135deg)",
  }[edgePos.edge] || "rotate(0deg)";

  // Edge hint highlight strip
  const edgeHintStyle = showEdgeHint ? {
    position: "fixed", zIndex: 62, pointerEvents: "none",
    background: "linear-gradient(to " + (showEdgeHint === "left" ? "right" : showEdgeHint === "right" ? "left" : showEdgeHint === "top" ? "bottom" : "top") + ", rgba(59,130,246,0.3), transparent)",
    ...(showEdgeHint === "left" ? { left: 0, top: 0, width: 40, height: "100vh" } :
       showEdgeHint === "right" ? { right: 0, top: 0, width: 40, height: "100vh" } :
       showEdgeHint === "top" ? { top: 0, left: 0, height: 40, width: "100vw" } :
       { bottom: 0, left: 0, height: 40, width: "100vw" }),
  } : null;

  return (
    <>
      <canvas
        ref={canvasRef}
        className={`fixed inset-0 z-[60] ${drawMode ? (activeTool === "eraser" ? "cursor-pointer" : "cursor-crosshair") : "pointer-events-none"}`}
        width={window.innerWidth}
        height={window.innerHeight}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />

      {/* Edge hint glow when dragging panel near edge */}
      {edgeHintStyle && <div style={edgeHintStyle} />}

      {mode === "collapsed" ? (
        /* Small icon at edge */
        <div
          style={
            draggingCollapsed && collapsedDragPos
              ? { position: "fixed", left: collapsedDragPos.x - BTN_SIZE / 2, top: collapsedDragPos.y - BTN_SIZE / 2, zIndex: 61, cursor: "grabbing" }
              : { position: "fixed", ...getCollapsedStyle(edgePos.edge, edgePos.pos), zIndex: 61,
                  transition: "opacity 0.5s ease, left 0.3s, top 0.3s, right 0.3s, bottom 0.3s",
                  opacity: faded ? 0.5 : 1, cursor: "grab" }
          }
          onMouseDown={handleCollapsedMouseDown}
          onMouseEnter={() => { setFaded(false); if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current); }}
          onMouseLeave={() => resetFadeTimer()}
          title="Click to open, drag to move"
        >
          <div
            className={`relative flex items-center justify-center rounded-full shadow-lg border ${
              drawMode ? "bg-green-500 text-white border-green-600" : "bg-card text-foreground border-border hover:bg-muted"
            }`}
            style={{ width: BTN_SIZE, height: BTN_SIZE }}
          >
            <Pencil className="w-4 h-4" style={{ transform: pencilRotation, transition: "transform 0.3s" }} />
            {drawings.length > 0 && (
              <span className="absolute w-2 h-2 rounded-full bg-red-500 animate-pulse" style={{ top: 2, right: 2 }} />
            )}
          </div>
        </div>
      ) : (
        /* Expanded floating panel */
        <div
          data-pents-panel
          className="bg-card border rounded-lg shadow-xl select-none"
          style={{
            position: "fixed", zIndex: 61, width: 210,
            left: draggingPanel && panelDragPos ? panelDragPos.x : panelPos.x,
            top: draggingPanel && panelDragPos ? panelDragPos.y : panelPos.y,
            transition: draggingPanel ? "none" : "left 0.2s, top 0.2s",
            opacity: draggingPanel && showEdgeHint ? 0.5 : 1,
          }}
        >
          {/* Drag handle + header */}
          <div
            className="flex items-center justify-between px-2 py-1.5 border-b bg-muted/50 rounded-t-lg cursor-grab active:cursor-grabbing"
            onMouseDown={handlePanelDragStart}
          >
            <div className="flex items-center gap-1.5">
              <GripHorizontal className="w-3.5 h-3.5 text-muted-foreground/50" />
              <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Pents</span>
            </div>
            <div className="flex items-center gap-0.5">
              <button
                onClick={handleMinimize}
                className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                title="Minimize to edge"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => {
                  setEnabled(false);
                  localStorage.setItem("setting_pents_enabled", "false");
                  window.dispatchEvent(new Event("settings-changed"));
                }}
                className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                title="Close Pents"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="p-2 space-y-2">
            <div className="flex gap-1 flex-wrap">
              {TOOLS.map((tool) => (
                <button
                  key={tool.id}
                  onClick={() => setActiveTool(tool.id)}
                  className={`p-1.5 rounded transition-colors ${
                    activeTool === tool.id ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                  title={tool.label}
                >
                  <tool.icon className="w-4 h-4" />
                </button>
              ))}
            </div>
            <div className="flex gap-1 flex-wrap">
              {COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => setStrokeColor(color)}
                  className={`w-5 h-5 rounded-full border-2 transition-transform ${
                    strokeColor === color ? "border-primary scale-125" : "border-border"
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
            <div className="flex gap-1 items-center">
              {WIDTHS.map((w) => (
                <button
                  key={w}
                  onClick={() => setStrokeWidth(w)}
                  className={`flex-1 flex items-center justify-center h-6 rounded transition-colors ${
                    strokeWidth === w ? "bg-primary/20 border border-primary" : "hover:bg-muted border border-transparent"
                  }`}
                  title={`${w}px`}
                >
                  <div className="rounded-full bg-foreground" style={{ width: Math.min(w * 1.5, 14), height: Math.min(w * 1.5, 14) }} />
                </button>
              ))}
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setDrawMode(!drawMode)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-medium transition-colors ${
                  drawMode ? "bg-green-500 text-white hover:bg-green-600" : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                title={drawMode ? "Drawing ON" : "Drawing OFF"}
              >
                <Power className="w-3.5 h-3.5" />
                {drawMode ? "Drawing" : "Paused"}
              </button>
              <button
                onClick={handleClear}
                className="flex items-center justify-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors"
                title="Clear all drawings"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
